import { useState } from "react";
import { NumberFormat } from "@design-system/NumberFormat/NumberFormat";
import { createCustomInputsBySize } from "@design-system/Input/inputCustomInputs";
import { ariaLabelledbyProps } from "@design-system/formControl/ariaLabelledbyProps";
import type { ValueControlProps } from "@design-system/formControl/formControl.types";
import { InputSize, toDsInputSize } from "@helpers/form";
import styles from "./MonetaryInput.module.scss";

const inputsBySize = createCustomInputsBySize();

interface MonetaryInputProps extends ValueControlProps<
    number | undefined,
    HTMLInputElement
> {
    className?: string;
    showCurrencySymbolIfEmpty?: boolean;
    allowNegative?: boolean;
    size?: InputSize;
    onFocus?: () => void;
    /** Id of a visible element elsewhere that names the input. */
    "aria-labelledby"?: string;
    // Selects the full contents on focus so the first keystroke replaces the
    // existing amount. Opt-in to avoid changing other MonetaryInput usages.
    selectOnFocus?: boolean;
}

/** Currency amount input. Clearing the field reports `undefined`. */
export const MonetaryInput: React.FC<MonetaryInputProps> = ({
    value,
    onValueChange,
    className,
    showCurrencySymbolIfEmpty,
    allowNegative,
    disabled,
    readOnly,
    size,
    name,
    ref,
    invalid,
    onBlur,
    onFocus,
    selectOnFocus,
    required,
    "aria-labelledby": ariaLabelledby,
}) => {
    const SizedInput = inputsBySize[toDsInputSize(size)];
    // A lone "-" has no number yet, so the parent reports `undefined` back.
    // A nil value lets react-number-format keep its own "-" instead of
    // reformatting to an empty input and losing the sign.
    const [isMinusPending, setIsMinusPending] = useState(false);

    // react-number-format defers its own caret handling into a setTimeout and
    // invokes this onFocus last, so selecting here sticks without extra timing
    // workarounds.
    const handleFocus: React.FocusEventHandler<HTMLInputElement> = (event) => {
        if (selectOnFocus) {
            event.currentTarget.select();
        }
        onFocus?.();
    };

    return (
        <NumberFormat
            value={isMinusPending ? undefined : (value ?? "")}
            onValueChange={(v) => {
                setIsMinusPending(v.value === "-");
                onValueChange(
                    Number.isNaN(v.floatValue) ? undefined : v.floatValue,
                );
            }}
            prefix={
                typeof value === "number" || showCurrencySymbolIfEmpty
                    ? "$ "
                    : ""
            }
            decimalScale={2}
            fixedDecimalScale={value !== undefined}
            allowEmptyFormatting
            thousandSeparator
            allowNegative={allowNegative}
            disabled={disabled}
            readOnly={readOnly}
            name={name}
            getInputRef={ref}
            aria-invalid={invalid || undefined}
            aria-required={required || undefined}
            {...ariaLabelledbyProps(ariaLabelledby)}
            customInput={SizedInput}
            className={
                className ? `${styles.input} ${className}` : styles.input
            }
            onBlur={() => {
                setIsMinusPending(false);
                onBlur?.();
            }}
            onFocus={handleFocus}
        />
    );
};
